import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatBytes } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { FileIcon, PlusIcon, UploadIcon } from '#/shared/ui/icons'

interface ProofDropzoneProps {
  files: ReadonlyArray<File>
  onAddFiles: (files: ReadonlyArray<File>) => void
  onRemoveFile: (index: number) => void
}

const ACCEPTED_TYPES = 'image/png,image/jpeg,application/pdf'

function useImagePreview(file: File): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!file.type.startsWith('image/')) {
      setUrl(undefined)
      return
    }
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  return url
}

function FileChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useTranslation()
  const previewUrl = useImagePreview(file)
  return (
    <div className="flex w-full items-center gap-2.5 rounded-md border border-gray-200 bg-white px-3 py-2.5">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          className="size-9 shrink-0 rounded object-cover"
        />
      ) : (
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100"
        >
          <FileIcon className="size-4 text-gray-500" />
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-gray-900">
          {file.name}
        </span>
        <span className="text-xs text-gray-500">{formatBytes(file.size)}</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-900"
      >
        {t('settlePayment.proof.remove')}
      </button>
    </div>
  )
}

function Dropzone({
  onOpenPicker,
  onAddFiles,
}: {
  onOpenPicker: () => void
  onAddFiles: (files: ReadonlyArray<File>) => void
}) {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const list = Array.from(e.dataTransfer.files)
    if (list.length > 0) onAddFiles(list)
  }
  return (
    <button
      type="button"
      onClick={onOpenPicker}
      onDragOver={(e) => {
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'flex w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-4 py-5 transition-colors',
        isDragging
          ? 'border-primary bg-blue-100'
          : 'border-gray-300 bg-gray-50 hover:bg-gray-100',
      )}
    >
      <span
        aria-hidden
        className="flex size-10 items-center justify-center rounded-full bg-paper-avatar"
      >
        <UploadIcon className="size-4 text-gray-900" />
      </span>
      <span className="text-sm font-medium text-gray-900">
        {t('settlePayment.proof.cta')}
      </span>
      <span className="text-xs text-gray-500">
        {t('settlePayment.proof.hint')}
      </span>
    </button>
  )
}

function AddMoreButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border border-primary px-3 text-sm font-medium text-primary transition-colors select-none hover:bg-blue-100"
    >
      <PlusIcon className="size-4 shrink-0" aria-hidden />
      {t('settlePayment.proof.ctaMore')}
    </button>
  )
}

export function ProofDropzone({
  files,
  onAddFiles,
  onRemoveFile,
}: ProofDropzoneProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const openPicker = () => inputRef.current?.click()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : []
    e.target.value = ''
    if (picked.length > 0) onAddFiles(picked)
  }

  return (
    <div className="flex w-full flex-col gap-1.5">
      <span className="text-xs text-gray-500">
        {t('settlePayment.proof.label')}
      </span>
      {files.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {files.map((file, index) => (
            <FileChip
              key={`${file.name}-${index}`}
              file={file}
              onRemove={() => onRemoveFile(index)}
            />
          ))}
          <AddMoreButton onClick={openPicker} />
        </div>
      ) : (
        <Dropzone onOpenPicker={openPicker} onAddFiles={onAddFiles} />
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  )
}
